// @ts-nocheck
import { Component } from '@theme/component';
import { requestIdleCallback } from '@theme/utilities';

const SITE_ID = 'ccwfx6';
const RESULTS_PER_PAGE = 8;
const ENHANCE_CONCURRENCY = 3;
const INFINITE_SCROLL_THRESHOLD = 4;
const IO_ROOT_MARGIN = '400px 0px';

/** @type {Map<string, object>} */
const productCache = new Map();

/** @type {Map<string, Promise<object|null>>} */
const productFetchInFlight = new Map();

function whenSwiperReady(callback) {
  if (typeof window.whenSwiperReady === 'function') {
    return window.whenSwiperReady(callback);
  }
  if (window.Swiper) {
    callback(window.Swiper);
    return Promise.resolve();
  }
  return Promise.resolve();
}

function escapeHtml(text) {
  if (!text) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatPrice(price) {
  if (!price) return '';
  return '$' + Number(price).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function getHandleFromUrl(url) {
  return url.split('/products/')[1]?.split('?')[0] || '';
}

function getBrandFromProduct(product) {
  const attrs = product?.attributes || {};
  return (
    attrs.mfield_new_my_fields_brand_name ||
    attrs.brand ||
    product?.mfield_new_my_fields_brand_name ||
    product?.brand ||
    ''
  );
}

function getHoverImageUrl(product, core) {
  const attrs = product?.attributes || {};
  const main = core?.imageUrl || core?.thumbnailImageUrl || '';
  const hover =
    attrs.ss_image_hover ||
    product?.ss_image_hover ||
    product?.hoverImageUrl ||
    '';
  if (!hover || hover === main) return '';
  return hover;
}

async function fetchProductDetails(handle) {
  if (!handle) return null;
  if (productCache.has(handle)) return productCache.get(handle);

  if (productFetchInFlight.has(handle)) {
    return productFetchInFlight.get(handle);
  }

  const promise = fetch(`/products/${handle}.js`, { priority: 'low' })
    .then((res) => (res.ok ? res.json() : null))
    .then((product) => {
      if (product) productCache.set(handle, product);
      return product;
    })
    .catch((err) => {
      console.error('Product fetch error:', err);
      return null;
    })
    .finally(() => {
      productFetchInFlight.delete(handle);
    });

  productFetchInFlight.set(handle, promise);
  return promise;
}

function recordRecentlyViewed(productId) {
  if (!productId || window.__abcRecentlyViewedRecorded) return;
  window.__abcRecentlyViewedRecorded = true;

  try {
    let list = JSON.parse(localStorage.getItem('recently_viewed_products') || '[]');
    list = list.filter((id) => String(id) !== String(productId));
    list.unshift(productId);
    localStorage.setItem('recently_viewed_products', JSON.stringify(list.slice(0, 20)));
  } catch (err) {
    console.warn('Recently viewed: localStorage unavailable', err);
  }
}

function buildRecommendUrl(tag, productId, page) {
  const params = new URLSearchParams({
    siteId: SITE_ID,
    tags: tag,
    products: String(productId),
    page: String(page),
    resultsPerPage: String(RESULTS_PER_PAGE),
  });
  return `https://${SITE_ID}.a.searchspring.io/boost/${SITE_ID}/recommend?${params}`;
}

function createProductCard(product) {
  const core = product?.mappings?.core || {};
  const handle = getHandleFromUrl(core?.url || '');
  const titleClean = core?.name || 'Product';
  const dataTitle = titleClean
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-*|-*$/g, '');
  const brand = escapeHtml(getBrandFromProduct(product));
  const mainImage = escapeHtml(core?.imageUrl || core?.thumbnailImageUrl || '');
  const hoverImage = escapeHtml(getHoverImageUrl(product, core) || mainImage);
  const name = escapeHtml(core?.name || 'Product');
  const url = escapeHtml(core?.url || '#');
  const variantId = escapeHtml(product?.attributes?.variant_id || '');
  const needsShopifyEnhance = !brand || getHoverImageUrl(product, core) === '';

  return `
  <div class="swiper-slide">
    <div class="main-product-card product-card"
        data-product-id="${escapeHtml(core?.uid || '')}"
        data-product-price="${escapeHtml(String(core?.price ?? ''))}"
        data-title="${escapeHtml(dataTitle || 'Product')}"
        data-handle="${escapeHtml(handle)}"
        ${needsShopifyEnhance ? '' : 'data-enhanced="true"'}>

      <div class="wishlist-button">
        <button
          class="wk-button wk-floating"
          data-product-handle="${escapeHtml(handle)}"
          data-variant-id="${variantId}"
          type="button"
        >
          <span class="wk-icon">
            <svg viewBox="0 0 64 64">
              <path d="M32.012,59.616c-1.119-.521-2.365-1.141-3.707-1.859a79.264,79.264,0,0,1-11.694-7.614C6.316,42,.266,32.6.254,22.076,0.244,12.358,7.871,4.506,17.232,4.5a16.661,16.661,0,0,1,11.891,4.99l2.837,2.889,2.827-2.9a16.639,16.639,0,0,1,11.874-5.02h0c9.368-.01,17.008,7.815,17.021,17.539,0.015,10.533-6.022,19.96-16.312,28.128a79.314,79.314,0,0,1-11.661,7.63C34.369,58.472,33.127,59.094,32.012,59.616Z"></path>
            </svg>
          </span>
          <span class="wk-text">Add to Wishlist</span>
        </button>
      </div>

      <a href="${url}" class="product-card__link">
        <div class="product-card__image">
          <img class="product-card__img custom-lazy"
            src="${mainImage}"
            alt="${name}"
            title="Image for ${name}"
            fetchpriority="low"
            loading="lazy"
            decoding="async"
          />
          <img class="product-card__img product-card__img--hover custom-lazyload"
            src="${hoverImage}"
            alt="${name}"
            title="Image for ${name}"
            fetchpriority="low"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div class="product-card__details" style="padding-top:10px;">
          <span class="product-card__vendor">${brand}</span>
          <span class="product-card__title">${name}</span>
          <span class="product-card__subtitle"></span>
          <span class="price-value product-card__price">
            <span class="price-value__current">${formatPrice(core?.price)}</span>
            <s title="Regular price" class="price-value__compare" data-acsb-hidden="true"></s>
          </span>
        </div>
      </a>
    </div>
  </div>`;
}

async function enhanceCardWithShopifyData(handle, cardEl) {
  if (!handle || cardEl.hasAttribute('data-enhanced')) return;

  const product = await fetchProductDetails(handle);
  if (!product) return;

  const images = product.images || [];
  const vendor = product.vendor || '';

  const vendorEl = cardEl.querySelector('.product-card__vendor');
  if (vendorEl && vendor && !vendorEl.textContent.trim()) {
    vendorEl.textContent = vendor;
  }

  if (images.length > 1) {
    const hoverImg = cardEl.querySelector('.product-card__img--hover');
    const mainImg = cardEl.querySelector('.product-card__img:not(.product-card__img--hover)');
    const mainSrc = mainImg?.getAttribute('src') || '';
    if (hoverImg && images[1] && images[1] !== mainSrc) {
      hoverImg.src = images[1];
    }
  }

  cardEl.setAttribute('data-enhanced', 'true');
}

class SearchspringProductRecommendations extends Component {
  /** @type {IntersectionObserver|null} */
  #io = null;
  /** @type {(() => void)|null} */
  #slideChangeHandler = null;
  #currentPage = 1;
  #isLoading = false;
  #hasMore = true;
  #initialized = false;
  #observeScheduled = false;
  /** @type {(() => void)|null} */
  #resizeHandler = null;

  static get observedAttributes() {
    return ['searchspring-tag', 'product-id'];
  }

  connectedCallback() {
    super.connectedCallback();
    recordRecentlyViewed(this.productId);
    this.#scheduleLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
    this.#io = null;
    this.#unbindSlideChange();
    this.#unbindResize();
    const swiper = this.#swiperEl?.swiper;
    if (swiper) {
      swiper.destroy(true, true);
    }
    this.#initialized = false;
    this.#observeScheduled = false;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#resetAndSchedule();
  }

  get productId() {
    return this.getAttribute('product-id') || '';
  }

  get searchspringTag() {
    return this.getAttribute('searchspring-tag') || '';
  }

  get #wrapper() {
    return this.closest('[data-ss-recommendations-wrapper]');
  }

  get #loader() {
    return this.refs.loader ?? this.querySelector('[ref="loader"]');
  }

  get #productsContainer() {
    return this.refs.products ?? this.querySelector('[ref="products"]');
  }

  get #swiperEl() {
    return this.querySelector('.swiper');
  }

  get #swiperInstance() {
    return this.#swiperEl?.swiper ?? null;
  }

  set #swiperInstance(instance) {
    if (this.#swiperEl) {
      this.#swiperEl.swiper = instance;
    }
  }

  #resetAndSchedule() {
    this.#io?.disconnect();
    this.#io = null;
    this.#unbindSlideChange();
    this.#unbindResize();
    const swiper = this.#swiperEl?.swiper;
    if (swiper) {
      swiper.destroy(true, true);
    }
    this.#currentPage = 1;
    this.#isLoading = false;
    this.#hasMore = true;
    this.#initialized = false;
    this.#observeScheduled = false;
    const container = this.#productsContainer;
    if (container) container.innerHTML = '';
    if (this.#wrapper) this.#wrapper.style.display = '';
    this.#scheduleLoad();
  }

  #scheduleLoad() {
    const wrapper = this.#wrapper || this;
    if (!wrapper || this.#observeScheduled) return;
    this.#observeScheduled = true;

    const run = () => {
      if (this.#initialized) return;
      this.#initialized = true;
      this.#loadProducts(1);
      this.#bindInfiniteScroll();
    };

    if ('IntersectionObserver' in window) {
      this.#io = new IntersectionObserver(
        (entries) => {
          if (!entries[0]?.isIntersecting) return;
          this.#io?.disconnect();
          this.#io = null;
          requestIdleCallback(run, { timeout: 2500 });
        },
        { root: null, rootMargin: IO_ROOT_MARGIN, threshold: 0.01 }
      );
      this.#io.observe(wrapper);
    } else {
      requestIdleCallback(run, { timeout: 2500 });
    }
  }

  #bindResize() {
    if (this.#resizeHandler) return;
    this.#resizeHandler = () => {
      const swiper = this.#swiperInstance;
      if (!swiper) return;
      const offset = this.#calcOffset();
      swiper.params.slidesOffsetBefore = offset;
      swiper.update();
    };
    window.addEventListener('resize', this.#resizeHandler, { passive: true });
  }

  #unbindResize() {
    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }
  }

  #calcOffset() {
    const alignSelector = this.#swiperEl?.getAttribute('carousel-offset-with');
    if (!alignSelector) return window.matchMedia('(min-width: 960px)').matches ? 104.4 : 20;

    const alignElement = document.querySelector(alignSelector);
    if (alignElement) {
      const rect = alignElement.getBoundingClientRect();
      return rect.left > 0 ? rect.left : 20;
    }
    return 20;
  }

  async #loadProducts(page) {
    if (this.#isLoading || !this.#hasMore) return;

    const tag = this.searchspringTag;
    const productId = this.productId;
    const container = this.#productsContainer;
    if (!container || !tag || !productId) return;

    this.#isLoading = true;
    const loader = this.#loader;
    if (page === 1 && loader) loader.classList.remove('hidden');

    try {
      const res = await fetch(buildRecommendUrl(tag, productId, page), { priority: 'low' });
      const data = await res.json();
      const results = data?.[0]?.results || [];

      if (!results.length) {
        this.#hasMore = false;
        if (page === 1 && this.#wrapper) {
          this.#wrapper.style.display = 'none';
        }
        return;
      }

      container.insertAdjacentHTML('beforeend', results.map(createProductCard).join(''));

      // Hide loader as soon as cards are in the DOM — do not wait for Shopify enhancement
      this.#hideLoader();

      requestAnimationFrame(() => {
        whenSwiperReady(() => this.#initOrUpdateSwiper());
      });

      this.#enhanceCardsInBackground(
        container.querySelectorAll('.product-card:not([data-enhanced])')
      );

      this.#currentPage = page + 1;
    } catch (err) {
      console.error('SearchSpring recommendations error:', err);
    } finally {
      this.#isLoading = false;
      this.#hideLoader();
    }
  }

  #hideLoader() {
    const loader = this.#loader;
    if (loader) loader.classList.add('hidden');
  }

  #enhanceCardsInBackground(cards) {
    const cardList = Array.from(cards);
    if (!cardList.length) return;

    const run = async () => {
      for (let i = 0; i < cardList.length; i += ENHANCE_CONCURRENCY) {
        const chunk = cardList.slice(i, i + ENHANCE_CONCURRENCY);
        await Promise.all(
          chunk.map((card) => {
            const handle = card.dataset.handle;
            return handle ? enhanceCardWithShopifyData(handle, card) : Promise.resolve();
          })
        );
      }
      requestAnimationFrame(() => {
        if (this.#swiperInstance) this.#swiperInstance.update();
      });
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(() => run(), { timeout: 1500 });
    } else {
      run();
    }
  }

  #initOrUpdateSwiper() {
    const swiperEl = this.#swiperEl;
    if (!swiperEl) return;

    const offset = this.#calcOffset();
    const scrollbarEl = this.querySelector('.swiper-scroll__scrollbar');
    const navNext = this.querySelector('.swiper-nav--next');
    const navPrev = this.querySelector('.swiper-nav--prev');

    if (this.#swiperInstance) {
      this.#swiperInstance.update();
      return;
    }

    whenSwiperReady((Swiper) => {
      if (swiperEl.swiper) return;

      const instance = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 32,
        slidesOffsetBefore: offset,
        observer: true,
        observeParents: true,
        watchOverflow: true,
        navigation: {
          nextEl: navNext,
          prevEl: navPrev,
        },
        scrollbar: {
          el: scrollbarEl,
          draggable: true,
        },
        breakpoints: {
          0: { slidesOffsetBefore: this.#calcOffset() },
          960: { slidesOffsetBefore: this.#calcOffset() },
        },
      });

      this.#swiperInstance = instance;
      this.#bindSlideChangeOnSwiper();
      this.#bindResize();
    });
  }

  #bindInfiniteScroll() {
    let attempts = 0;
    const maxAttempts = 60;

    const tick = () => {
      attempts += 1;
      if (this.#swiperInstance) {
        this.#bindSlideChangeOnSwiper();
        return;
      }
      if (attempts < maxAttempts) requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  }

  #bindSlideChangeOnSwiper() {
    const swiper = this.#swiperInstance;
    if (!swiper || this.#slideChangeHandler) return;

    this.#slideChangeHandler = () => {
      const totalSlides = swiper.slides.length;
      const currentIndex = swiper.activeIndex;
      if (currentIndex >= totalSlides - INFINITE_SCROLL_THRESHOLD) {
        this.#loadProducts(this.#currentPage);
      }
    };

    swiper.on('slideChange', this.#slideChangeHandler);
  }

  #unbindSlideChange() {
    const swiper = this.#swiperInstance;
    if (swiper && this.#slideChangeHandler) {
      swiper.off('slideChange', this.#slideChangeHandler);
    }
    this.#slideChangeHandler = null;
  }
}

if (!customElements.get('searchspring-product-recommendations')) {
  customElements.define('searchspring-product-recommendations', SearchspringProductRecommendations);
}
